import {
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    Image,
    Text,
    useToast,
} from "@chakra-ui/react";

import { AppDropdownSelect } from "components/AppDropdownSelect";
import AppInput from "components/AppInput";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageLoadDataPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch } = useContext(Store);
  const { user } = useAuthentication();
  const [bannerSrc, setBannerSrc] = useState(null);
  const { getInstitutions, uploadTaxPayersData } = useApi();
  const { data: institutions } = useCache("institutions", () =>
    getInstitutions(1)
  );
  const initialValues = useMemo(() => {
    return {
      institution: user?.user?.institution_id + "",
      csv: null,
      submit: null,
    };
  }, [user?.user]);
  const validationSchema = Yup.object().shape({
    institution: Yup.string().required("institution requis"),
    csv: Yup.mixed().required("csv requis"),
  });

  const { values, handleSubmit, isSubmitting, setFieldValue } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let formData: any = new FormData();
        formData.append("institution_id", values.institution);
        formData.append("csv_file", values.csv, values.csv.name);

        const rsp = await uploadTaxPayersData(formData);
        if (!rsp.error_code) {
          dispatch(
            showToast({
              toast: toast,
              message: "Nous avons bien reçu votre fichier ! Il est en cours de traitement, merci de votre patience..",
              status: toastTypes.success,
            })
          );
          navigate("/tax-payers");
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: "Quelque chose s'est mal passé",
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const url = URL.createObjectURL(file);
    setBannerSrc(url);
    setFieldValue("csv", file);
  };
  return (
    <>
      <Flex
        gap="18px"
        bg="very_light_gray"
        flex={1}
        flexDirection="column"
        py="20px"
        px="30px"
        minH="100vh"
      >
        <Box>
          <Flex gap="4px" flexDirection="column">
            <Flex justifyContent="center" alignItems="center">
              <Heading size="text2xl" as="h4" fontFamily="Arimo">
                Charger les Débiteurs
              </Heading>
              <Flex
                pl="56px"
                pr="62px"
                gap="9px"
                flex={1}
                justifyContent="flex-end"
                px={{ md: 0, base: "20px" }}
              >
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = "/sample_taxpayers.csv"; // replace with the actual path to your sample CSV
                    link.download = "sample_taxpayers.csv"; // name of the downloaded file
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Télécharger un exemple
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Box>
        <form
          onSubmit={handleSubmit}
          style={{
            flexDirection: "column",
            margin: "0px",
            alignItems: "center",
          }}
        >
          <Flex flexDirection="column" alignItems="center">
            <Flex
              gap="16px"
              bg="white"
              boxShadow="xl"
              w="100%"
              flexDirection="column"
              px={{ base: "20px", sm: "24px" }}
              py={{ base: "20px", sm: "24px" }}
              mx={{ md: "62px", base: "0px" }}
              borderRadius="16px"
            >
              <Flex gap="8px" flexDirection="column" alignItems="start">
                <HStack>
                  <Text color="gray_description" mt="16px">
                    Fichier CSV contenant les Débiteurs
                  </Text>
                  <span style={{ color: "red" }}>*</span>
                </HStack>
                <AppInput
                  type="file"
                  accept=".csv"
                  borderWidth="1px"
                  alignSelf="stretch"
                  borderRadius="8px"
                  name="csv"
                  id="csv"
                  onChange={(e: any) => handleFileChange(e)}
                  display={bannerSrc ? "flex": "none"}
                />
                {!bannerSrc && <Flex
                  gap="16px"
                  alignSelf="stretch"
                  alignItems="start"
                  flexDirection={{ md: "row", base: "column" }}
                >
                  <Image
                    src={
                      bannerSrc ? bannerSrc : "images/csv_import_preview.png"
                    }
                    alt="Banner Image"
                    h="80px"
                    alignSelf="center"
                    w={{ md: "25%", base: "100%" }}
                    fit="cover"
                    borderRadius="12px"
                    onClick={() => document.getElementById("csv").click()}
                  />
                </Flex>}
              </Flex>
              {!user?.user.institution_id && (
                <Flex gap="4px" flexDirection="column" alignItems="start">
                  <HStack>
                    <Text color="gray_description">Institution</Text>
                    <span style={{ color: "red" }}>*</span>
                  </HStack>
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    width={"100%"}
                  >
                    <AppDropdownSelect
                      name="institution"
                      data={institutions}
                      value={values.institution}
                      onChange={setFieldValue}
                      placeholder="Veuillez sélectionner une institution"
                    />
                  </Flex>
                </Flex>
              )}
            </Flex>
            <Flex mt="24px" gap="12px">
              <Button
                size="4xl"
                color="gray_description"
                fontFamily="Arimo"
                fontWeight={700}
                minW="106px"
                borderRadius="8px"
                onClick={() => window.history.back()}
              >
                Annuler
              </Button>
              <Button
                size="4xl"
                colorScheme="red_origin"
                fontFamily="Arimo"
                fontWeight={700}
                minW="132px"
                borderRadius="8px"
                type="submit"
                isLoading={isSubmitting}
              >
                Enregistrer
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </>
  );
}
